import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { FilePageNavigationStatus } from '../../../../generated.commonjs/plan.reviewer_pb';
import { PlanReviewerService } from '../../../shared/plan-reviewer.service';
import { FileComplianceSummaryComponent } from './file-compliance-summary.component';
import { FileOverviewComponent } from './file-overview.component';

export type FileViewTab = 'overview' | 'compliance';

/** Route-owned file workspace containing the Overview and Compliance tabs. */
@Component({
  selector: 'app-file-view',
  imports: [CommonModule, MatTabsModule, FileOverviewComponent, FileComplianceSummaryComponent],
  templateUrl: './file-view.component.html',
  styleUrls: ['./file-view.component.scss'],
})
export class FileViewComponent implements OnChanges {
  private router = inject(Router);
  private planReviewerService = inject(PlanReviewerService);
  private statusLoadGeneration = 0;

  @Input({ required: true }) projectId = '';
  @Input({ required: true }) fileId = '';
  @Input() selectedTab: FileViewTab = 'overview';
  @Output() pageStatusesChange = new EventEmitter<FilePageNavigationStatus.AsObject[]>();

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.projectId &&
      this.fileId &&
      this.selectedTab === 'compliance' &&
      (changes['projectId'] || changes['fileId'] || changes['selectedTab'])
    ) {
      void this.loadPageStatuses();
    }
  }

  get selectedIndex(): number {
    return this.selectedTab === 'compliance' ? 1 : 0;
  }

  onSelectedIndexChange(index: number): void {
    const tab: FileViewTab = index === 1 ? 'compliance' : 'overview';
    if (tab === this.selectedTab) {
      return;
    }
    void this.router.navigate(['/projects', this.projectId, 'files', this.fileId, tab]);
  }

  onPageStatusesChange(statuses: FilePageNavigationStatus.AsObject[]): void {
    this.pageStatusesChange.emit(statuses);
  }

  private async loadPageStatuses(): Promise<void> {
    const generation = ++this.statusLoadGeneration;
    try {
      const response = await this.planReviewerService.getFileOverviewSummary(this.projectId, this.fileId);
      if (generation === this.statusLoadGeneration) {
        this.pageStatusesChange.emit(response.toObject().pageStatusesList);
      }
    } catch {
      if (generation === this.statusLoadGeneration) {
        this.pageStatusesChange.emit([]);
      }
    }
  }
}
